/**
 * #/substrate — Saoirse Console operator door (Phase 1: READ-ONLY browse).
 *
 * 28-city substrate map + city list + search, all DB-driven:
 *  - cities, counts, centroids, bounds derived from list_all_substrate_for_editor()
 *  - per-city rows from list_substrate_for_editor(p_city)
 *  - provider pins from list_providers_for_editor(bbox derived from city extent)
 *  - kind legend derived from loaded rows; unknown kinds render loud magenta
 *    with a warning banner — never silent gray (design 2026-06-10 §2)
 *
 * Interaction model ported from the retired substrate-editor.html /
 * substrate-map-all.html pages: dark sidebar, city drill-in, kind legend as
 * filter, streets⇄satellite toggle, fit-to-scope. Substrate renders as a
 * GeoJSON circle layer (1,482 rows — DOM markers would crawl); providers are
 * DOM diamond markers (few per city, and they survive style switches).
 *
 * Search: in the all-cities view it matches city names AND landmark titles
 * globally; inside a city it filters that city's rows by title/kind. Search
 * filters the sidebar list; kind chips filter both list and map.
 *
 * Guard: requireSuperAdmin (UX gate). Non-operators get a friendly notice +
 * redirect to #/edit. Data access is read-only through RPCs that are already
 * anon-executable; nothing here writes — Phase 2 adds writes behind the held
 * RLS migration.
 */
import type { FeatureCollection, Point } from 'geojson';
import type { ExpressionSpecification, FilterSpecification, GeoJSONSource } from 'mapbox-gl';
import { mapboxgl } from '../lib/mapbox-helpers';
import { requireSuperAdmin, NotSuperAdminError } from '../lib/operator-guard';
import {
  fetchAllSubstrate,
  fetchCitySubstrate,
  fetchProvidersInBbox,
  buildCityIndex,
  cityProviderBbox,
  type SubstrateRow,
  type SubstrateCityRow,
  type ProviderRow,
  type CityIndexEntry,
} from '../lib/substrate-api';
import {
  deriveKinds,
  normalizeKind,
  providerTypeColor,
  KNOWN_PROVIDER_TYPE_COLORS,
  UNKNOWN_KIND_COLOR,
  type KindInfo,
} from '../lib/kinds';
import '../styles/substrate.css';

const STYLE_STREETS = 'mapbox://styles/mapbox/streets-v12';
const STYLE_SATELLITE = 'mapbox://styles/mapbox/satellite-streets-v12';
const SOURCE_ID = 'substrate';
const LAYER_ID = 'substrate-circles';
const GLOBAL_SEARCH_CAP = 80;

type SubstrateProps = {
  id: string;
  title: string;
  kind: string;
  salience: number;
  city: string;
};

type View =
  | { mode: 'overview' }
  | { mode: 'city-loading'; entry: CityIndexEntry }
  | { mode: 'city-error'; entry: CityIndexEntry; message: string }
  | {
      mode: 'city';
      entry: CityIndexEntry;
      rows: SubstrateRow[];
      providers: ProviderRow[];
      providerError: string | null;
    };

function esc(value: unknown): string {
  const entities: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return String(value ?? '').replace(/[&<>"']/g, (c) => entities[c] ?? c);
}

function fmt(n: number): string {
  return n.toLocaleString();
}

function renderNotOperator(container: HTMLElement): void {
  container.innerHTML = `
    <main class="container narrow">
      <h1>Operator area</h1>
      <p class="lead">The substrate console is the operator side of this tool.
        You're signed in, but this account doesn't have operator access.</p>
      <p>Taking you to your profile editor in a moment — or head there now:
        <a href="#/edit">edit your profile</a>.</p>
    </main>
  `;
  window.setTimeout(() => {
    const path = window.location.hash.replace(/^#/, '') || '/';
    if (path === '/substrate') window.location.hash = '#/edit';
  }, 2500);
}

export async function renderSubstrate(container: HTMLElement): Promise<void> {
  let sessionEmail = '';
  try {
    const session = await requireSuperAdmin();
    sessionEmail = session.user.email ?? session.user.id;
  } catch (err) {
    if (err instanceof NotSuperAdminError) renderNotOperator(container);
    // Signed out: requireSession already redirected to #/signin.
    return;
  }

  container.innerHTML = `
    <div class="sub-console">
      <aside class="sub-sidebar">
        <header class="sub-header">
          <h1>Saoirse Console</h1>
          <div class="sub-subtitle">Substrate <span class="sub-readonly-badge">read-only · phase 1</span></div>
          <div class="sub-session">${esc(sessionEmail)} · <a href="#/edit">profile</a></div>
        </header>
        <div class="sub-search-row">
          <input id="sub-search" type="search" placeholder="Search cities &amp; landmarks…" autocomplete="off" />
        </div>
        <nav id="sub-panel" class="sub-panel" aria-live="polite"></nav>
        <div id="sub-legend" class="sub-legend"></div>
        <footer class="sub-foot">
          <span id="sub-stats"></span>
          <div class="sub-foot-buttons">
            <button id="sub-fit" type="button" title="Fit map to current scope">Fit</button>
            <button id="sub-style" type="button" title="Toggle map style">Satellite</button>
          </div>
        </footer>
      </aside>
      <div class="sub-map">
        <div id="sub-map-canvas" class="sub-map"></div>
        <div id="sub-loading" class="sub-loading">Loading substrate…</div>
      </div>
    </div>
  `;

  // The shell was just written to innerHTML, so these always resolve; the
  // assert keeps TypeScript's null-tracking out of every closure below.
  function q<T extends HTMLElement>(selector: string): T {
    const el = container.querySelector<T>(selector);
    if (!el) throw new Error(`substrate shell missing ${selector}`);
    return el;
  }
  const mapEl = q<HTMLElement>('#sub-map-canvas');
  const sidebarEl = q<HTMLElement>('.sub-sidebar');
  const panelEl = q<HTMLElement>('#sub-panel');
  const legendEl = q<HTMLElement>('#sub-legend');
  const statsEl = q<HTMLElement>('#sub-stats');
  const searchEl = q<HTMLInputElement>('#sub-search');
  const loadingEl = q<HTMLElement>('#sub-loading');
  const fitBtn = q<HTMLButtonElement>('#sub-fit');
  const styleBtn = q<HTMLButtonElement>('#sub-style');

  // ---- state ----
  let allRows: SubstrateCityRow[] = [];
  let cityIndex: CityIndexEntry[] = [];
  let kinds: KindInfo[] = [];
  const disabledKinds = new Set<string>();
  let view: View = { mode: 'overview' };
  let searchTerm = '';
  let showProviders = true;
  let satellite = false;
  let loadSeq = 0;
  let lastGlobalHits: SubstrateCityRow[] = [];
  let lastRenderedRows: SubstrateRow[] = [];
  let providerMarkers: mapboxgl.Marker[] = [];

  // ---- map ----
  const map = new mapboxgl.Map({
    container: mapEl,
    style: STYLE_STREETS,
    center: [9.5, 45.0],
    zoom: 4,
  });
  map.addControl(new mapboxgl.NavigationControl(), 'top-right');

  function emptyCollection(): FeatureCollection<Point, SubstrateProps> {
    return { type: 'FeatureCollection', features: [] };
  }

  function ensureLayers(): void {
    if (map.getSource(SOURCE_ID)) return;
    map.addSource(SOURCE_ID, { type: 'geojson', data: emptyCollection() });
    map.addLayer({
      id: LAYER_ID,
      type: 'circle',
      source: SOURCE_ID,
      paint: {
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          4, 2.5,
          10, 4.5,
          14, 7,
        ] as ExpressionSpecification,
        'circle-color': UNKNOWN_KIND_COLOR,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1,
        'circle-opacity': 0.92,
      },
    });
  }

  // Layers + paint live on the style, so every style.load (initial load AND
  // streets⇄satellite switches) must rebuild them from current state.
  map.on('style.load', () => {
    ensureLayers();
    applyKindPaint();
    applyKindFilter();
    refreshMapData();
  });

  map.on('click', LAYER_ID, (e) => {
    const feature = e.features?.[0];
    if (!feature) return;
    const props = (feature.properties ?? {}) as Record<string, unknown>;
    openSubstratePopup(
      [e.lngLat.lng, e.lngLat.lat],
      {
        id: String(props.id ?? ''),
        title: String(props.title ?? ''),
        kind: String(props.kind ?? ''),
        salience: Number(props.salience ?? 0),
        city: String(props.city ?? ''),
      },
    );
  });
  map.on('mouseenter', LAYER_ID, () => {
    map.getCanvas().style.cursor = 'pointer';
  });
  map.on('mouseleave', LAYER_ID, () => {
    map.getCanvas().style.cursor = '';
  });

  function scopeRows(): Array<SubstrateRow & { city?: string | null }> {
    return view.mode === 'city' ? view.rows : allRows;
  }

  function enabledKindKeys(): string[] {
    return kinds.filter((k) => !disabledKinds.has(k.kind)).map((k) => k.kind);
  }

  function applyKindPaint(): void {
    if (!map.getLayer(LAYER_ID)) return;
    if (kinds.length === 0) {
      map.setPaintProperty(LAYER_ID, 'circle-color', UNKNOWN_KIND_COLOR);
      return;
    }
    const pairs = kinds.flatMap((k) => [k.kind, k.color]);
    const expr = ['match', ['get', 'kind'], ...pairs, UNKNOWN_KIND_COLOR] as ExpressionSpecification;
    map.setPaintProperty(LAYER_ID, 'circle-color', expr);
  }

  function applyKindFilter(): void {
    if (!map.getLayer(LAYER_ID)) return;
    const filter = ['in', ['get', 'kind'], ['literal', enabledKindKeys()]] as FilterSpecification;
    map.setFilter(LAYER_ID, filter);
  }

  function refreshMapData(): void {
    const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    if (!source) return;
    const features = scopeRows()
      .filter((r) => Number.isFinite(r.longitude) && Number.isFinite(r.latitude))
      .map((r) => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [r.longitude, r.latitude] },
        properties: {
          id: r.id,
          title: r.title,
          kind: normalizeKind(r.kind),
          salience: r.salience,
          city: 'city' in r && r.city ? r.city : view.mode === 'city' ? view.entry.name : '',
        },
      }));
    source.setData({ type: 'FeatureCollection', features });
  }

  function openSubstratePopup(lngLat: [number, number], props: SubstrateProps): void {
    const cityIdx = view.mode === 'overview' ? cityIndex.findIndex((c) => c.name === props.city) : -1;
    const html = `
      <div class="sub-pop-title">${esc(props.title)}</div>
      <div class="sub-pop-meta">${esc(props.kind)} · salience ${esc(props.salience)}${
        props.city ? ` · ${esc(props.city)}` : ''
      }</div>
      <div class="sub-pop-id">${esc(props.id)}</div>
      ${cityIdx >= 0 ? `<button class="sub-pop-open" data-popup-city="${cityIdx}">Open ${esc(props.city)} →</button>` : ''}
    `;
    const popup = new mapboxgl.Popup({ offset: 8, maxWidth: '300px' }).setLngLat(lngLat).setHTML(html).addTo(map);
    const openBtn = popup.getElement()?.querySelector<HTMLButtonElement>('[data-popup-city]');
    if (openBtn) {
      openBtn.addEventListener('click', () => {
        const entry = cityIndex[Number(openBtn.dataset.popupCity)];
        popup.remove();
        if (entry) void selectCity(entry);
      });
    }
  }

  function providerPopupHtml(p: ProviderRow): string {
    const typeKnown = p.business_type !== null && p.business_type in KNOWN_PROVIDER_TYPE_COLORS;
    const badges = [
      p.saoirse_recommended ? '<span class="sub-pop-badge sub-pop-badge-rec">recommended</span>' : '',
      p.saoirse_excluded ? '<span class="sub-pop-badge sub-pop-badge-exc">excluded</span>' : '',
      typeKnown ? '' : `<span class="sub-pop-badge sub-pop-badge-unknown">unrecognized type</span>`,
    ].join('');
    return `
      <div class="sub-pop-title">◆ ${esc(p.business_name)}</div>
      <div class="sub-pop-meta">${esc(p.business_type ?? '(no type)')} · weight ${esc(p.saoirse_weight ?? '—')}</div>
      <div>${badges}</div>
      <div class="sub-pop-id">${esc(p.id)}</div>
      <div class="sub-pop-note">Provider pins are read-only in Phase 1.</div>
    `;
  }

  function renderProviderMarkers(): void {
    providerMarkers.forEach((m) => m.remove());
    providerMarkers = [];
    if (view.mode !== 'city' || !showProviders) return;
    for (const p of view.providers) {
      if (!Number.isFinite(p.longitude) || !Number.isFinite(p.latitude)) continue;
      const el = document.createElement('div');
      el.className = 'sub-provider-pin';
      el.style.background = providerTypeColor(p.business_type);
      if (!(p.business_type !== null && p.business_type in KNOWN_PROVIDER_TYPE_COLORS)) {
        el.classList.add('sub-provider-pin-unknown');
      }
      el.title = p.business_name;
      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([p.longitude, p.latitude])
        .setPopup(new mapboxgl.Popup({ offset: 10, maxWidth: '320px' }).setHTML(providerPopupHtml(p)))
        .addTo(map);
      providerMarkers.push(marker);
    }
  }

  // ---- sidebar renderers ----

  function rowMatchesSearch(row: { title: string; kind: string }, term: string): boolean {
    if (!term) return true;
    return (
      row.title.toLowerCase().includes(term) || normalizeKind(row.kind).toLowerCase().includes(term)
    );
  }

  function kindDot(kind: string): string {
    const info = kinds.find((k) => k.kind === normalizeKind(kind));
    const color = info ? info.color : UNKNOWN_KIND_COLOR;
    return `<span class="sub-dot" style="background:${esc(color)}"></span>`;
  }

  function renderPanel(): void {
    const term = searchTerm.trim().toLowerCase();

    if (view.mode === 'overview') {
      searchEl.placeholder = 'Search cities & landmarks…';
      if (!term) {
        const rows = cityIndex
          .map(
            (c, i) => `
              <button class="sub-city-row" data-ci="${i}" type="button">
                <span class="sub-city-name">${esc(c.name)}${c.isNoCityBucket ? ' ⚠' : ''}</span>
                <span class="sub-count">${fmt(c.count)}</span>
              </button>`,
          )
          .join('');
        panelEl.innerHTML = `
          <div class="sub-section-title">Cities (${cityIndex.length})</div>
          ${rows || '<div class="sub-muted">No substrate rows found.</div>'}
        `;
        return;
      }
      const cityHits = cityIndex
        .map((c, i) => ({ c, i }))
        .filter(({ c }) => c.name.toLowerCase().includes(term));
      lastGlobalHits = allRows.filter((r) => rowMatchesSearch(r, term)).slice(0, GLOBAL_SEARCH_CAP);
      const totalHits = allRows.reduce((n, r) => n + (rowMatchesSearch(r, term) ? 1 : 0), 0);
      const cityHtml = cityHits
        .map(
          ({ c, i }) => `
            <button class="sub-city-row" data-ci="${i}" type="button">
              <span class="sub-city-name">${esc(c.name)}</span>
              <span class="sub-count">${fmt(c.count)}</span>
            </button>`,
        )
        .join('');
      const hitHtml = lastGlobalHits
        .map(
          (r, i) => `
            <button class="sub-row" data-g="${i}" type="button">
              ${kindDot(r.kind)}
              <span class="sub-row-title">${esc(r.title)}
                <span class="sub-row-sub">${esc(r.city ?? '(no city)')} · ${esc(normalizeKind(r.kind))}</span>
              </span>
              <span class="sub-sal">S${esc(r.salience)}</span>
            </button>`,
        )
        .join('');
      panelEl.innerHTML = `
        ${cityHits.length ? `<div class="sub-section-title">Cities</div>${cityHtml}` : ''}
        <div class="sub-section-title">Landmarks (${fmt(totalHits)})</div>
        ${hitHtml || '<div class="sub-muted">No landmarks match.</div>'}
        ${totalHits > lastGlobalHits.length ? `<div class="sub-muted">Showing first ${GLOBAL_SEARCH_CAP} — refine the search.</div>` : ''}
      `;
      return;
    }

    // city-scoped views
    const entry = view.entry;
    searchEl.placeholder = `Search ${entry.name}…`;
    const head = `
      <button class="sub-back" data-back="1" type="button">← All cities</button>
      <div class="sub-city-head">
        <h2>${esc(entry.name)}</h2>
        <div class="sub-city-meta" id="sub-city-meta"></div>
      </div>
    `;

    if (view.mode === 'city-loading') {
      panelEl.innerHTML = `${head}<div class="sub-muted">Loading ${esc(entry.name)}…</div>`;
      return;
    }
    if (view.mode === 'city-error') {
      panelEl.innerHTML = `
        ${head}
        <div class="sub-error-box">
          Couldn't load ${esc(entry.name)}: ${esc(view.message)}
          <br /><button data-retry-city="1" type="button">Retry</button>
        </div>
      `;
      return;
    }

    const providerCount = view.providers.length;
    lastRenderedRows = view.rows
      .filter((r) => !disabledKinds.has(normalizeKind(r.kind)) && rowMatchesSearch(r, term))
      .sort((a, b) => b.salience - a.salience || a.title.localeCompare(b.title));
    const rowsHtml = lastRenderedRows
      .map(
        (r, i) => `
          <button class="sub-row" data-ri="${i}" type="button">
            ${kindDot(r.kind)}
            <span class="sub-row-title">${esc(r.title)}
              <span class="sub-row-sub">${esc(normalizeKind(r.kind))}</span>
            </span>
            <span class="sub-sal" title="salience">S${esc(r.salience)}</span>
          </button>`,
      )
      .join('');
    panelEl.innerHTML = `
      ${head}
      <label class="sub-prov-line">
        <input type="checkbox" id="sub-prov-toggle" ${showProviders ? 'checked' : ''} />
        Providers <span class="sub-count">${fmt(providerCount)}</span>
      </label>
      ${view.providerError ? `<div class="sub-warn-text">⚠ providers failed to load: ${esc(view.providerError)}</div>` : ''}
      <div class="sub-section-title">Landmarks (${fmt(lastRenderedRows.length)}${
        lastRenderedRows.length !== view.rows.length ? ` of ${fmt(view.rows.length)}` : ''
      })</div>
      ${rowsHtml || '<div class="sub-muted">Nothing matches the current search/filters.</div>'}
    `;
    const meta = panelEl.querySelector<HTMLElement>('#sub-city-meta');
    if (meta) {
      meta.textContent = `${fmt(view.rows.length)} landmarks · ${fmt(providerCount)} providers in view`;
    }
  }

  function renderLegend(): void {
    const unknown = kinds.filter((k) => !k.known);
    const chips = kinds
      .map(
        (k) => `
          <button class="sub-kind-chip${disabledKinds.has(k.kind) ? ' sub-off' : ''}${
            k.known ? '' : ' sub-kind-unknown'
          }" data-kind="${esc(k.kind)}" type="button" title="${k.known ? esc(k.kind) : 'Unrecognized kind — not in the known vocabulary'}">
            <span class="sub-dot" style="background:${esc(k.color)}"></span>
            <span class="sub-chip-name">${esc(k.kind)}</span>
            <span class="sub-count">${fmt(k.count)}</span>
          </button>`,
      )
      .join('');
    legendEl.innerHTML = `
      ${
        unknown.length
          ? `<div class="sub-unknown-warn">⚠ ${unknown.length} kind${unknown.length > 1 ? 's' : ''} not in the known vocabulary (shown bright magenta): ${unknown
              .map((k) => esc(k.kind))
              .join(', ')}</div>`
          : ''
      }
      <div class="sub-legend-head">
        <span>Kinds</span>
        <span>
          <button data-kind-all="1" type="button">all</button> ·
          <button data-kind-none="1" type="button">none</button>
        </span>
      </div>
      ${chips || '<div class="sub-muted">No kinds yet.</div>'}
    `;
  }

  function updateStats(): void {
    if (view.mode === 'city') {
      statsEl.textContent = `${view.entry.name}: ${fmt(view.rows.length)} landmarks · ${fmt(view.providers.length)} providers`;
      return;
    }
    if (view.mode === 'overview') {
      statsEl.textContent = `${fmt(allRows.length)} landmarks · ${cityIndex.length} cities`;
      return;
    }
    statsEl.textContent = view.entry.name;
  }

  function refreshAll(): void {
    kinds = deriveKinds(scopeRows());
    applyKindPaint();
    applyKindFilter();
    refreshMapData();
    renderPanel();
    renderLegend();
    updateStats();
    renderProviderMarkers();
  }

  // ---- navigation ----

  function allBounds(): mapboxgl.LngLatBounds {
    const bounds = new mapboxgl.LngLatBounds();
    for (const c of cityIndex) {
      bounds.extend(c.bounds[0]);
      bounds.extend(c.bounds[1]);
    }
    return bounds;
  }

  function fitToScope(): void {
    if (view.mode === 'overview') {
      const bounds = allBounds();
      if (!bounds.isEmpty()) map.fitBounds(bounds, { padding: 60, duration: 800 });
    } else {
      map.fitBounds(view.entry.bounds, { padding: 80, duration: 800, maxZoom: 16 });
    }
  }

  async function selectCity(entry: CityIndexEntry): Promise<void> {
    const seq = ++loadSeq;
    view = { mode: 'city-loading', entry };
    renderPanel();
    updateStats();
    map.fitBounds(entry.bounds, { padding: 80, duration: 800, maxZoom: 16 });
    let providerError: string | null = null;
    try {
      const rowsPromise = entry.isNoCityBucket
        ? Promise.resolve(
            allRows
              .filter((r) => !r.city || r.city.trim() === '')
              .map(({ id, title, kind, latitude, longitude, salience }) => ({
                id, title, kind, latitude, longitude, salience,
              })),
          )
        : fetchCitySubstrate(entry.name);
      const providersPromise = fetchProvidersInBbox(cityProviderBbox(entry)).catch((err: unknown) => {
        providerError = err instanceof Error ? err.message : String(err);
        return [] as ProviderRow[];
      });
      const [rows, providers] = await Promise.all([rowsPromise, providersPromise]);
      if (seq !== loadSeq) return; // superseded by a later navigation
      view = { mode: 'city', entry, rows, providers, providerError };
    } catch (err) {
      if (seq !== loadSeq) return;
      view = { mode: 'city-error', entry, message: err instanceof Error ? err.message : String(err) };
    }
    refreshAll();
  }

  function backToOverview(): void {
    loadSeq += 1; // invalidate any in-flight city load
    view = { mode: 'overview' };
    refreshAll();
    fitToScope();
  }

  function flyToRow(row: SubstrateRow, city: string): void {
    map.flyTo({
      center: [row.longitude, row.latitude],
      zoom: Math.max(map.getZoom(), 16),
      duration: 700,
    });
    openSubstratePopup([row.longitude, row.latitude], {
      id: row.id,
      title: row.title,
      kind: normalizeKind(row.kind),
      salience: row.salience,
      city,
    });
  }

  // ---- events (delegated) ----

  sidebarEl.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>(
      '[data-ci],[data-g],[data-ri],[data-back],[data-retry-city],[data-kind],[data-kind-all],[data-kind-none]',
    );
    if (!target) return;
    const d = target.dataset;
    if (d.ci !== undefined) {
      const entry = cityIndex[Number(d.ci)];
      if (entry) void selectCity(entry);
    } else if (d.g !== undefined) {
      const row = lastGlobalHits[Number(d.g)];
      if (row) flyToRow(row, row.city ?? '');
    } else if (d.ri !== undefined) {
      const row = lastRenderedRows[Number(d.ri)];
      if (row) flyToRow(row, view.mode === 'overview' ? '' : view.entry.name);
    } else if (d.back !== undefined) {
      backToOverview();
    } else if (d.retryCity !== undefined) {
      if (view.mode !== 'overview') void selectCity(view.entry);
    } else if (d.kind !== undefined) {
      if (disabledKinds.has(d.kind)) disabledKinds.delete(d.kind);
      else disabledKinds.add(d.kind);
      applyKindFilter();
      renderLegend();
      renderPanel();
    } else if (d.kindAll !== undefined) {
      disabledKinds.clear();
      applyKindFilter();
      renderLegend();
      renderPanel();
    } else if (d.kindNone !== undefined) {
      kinds.forEach((k) => disabledKinds.add(k.kind));
      applyKindFilter();
      renderLegend();
      renderPanel();
    }
  });

  sidebarEl.addEventListener('change', (e) => {
    const target = e.target as HTMLElement;
    if (target.id === 'sub-prov-toggle') {
      showProviders = (target as HTMLInputElement).checked;
      renderProviderMarkers();
    }
  });

  searchEl.addEventListener('input', () => {
    searchTerm = searchEl.value;
    renderPanel();
  });

  fitBtn.addEventListener('click', fitToScope);

  styleBtn.addEventListener('click', () => {
    satellite = !satellite;
    styleBtn.textContent = satellite ? 'Streets' : 'Satellite';
    // style.load handler rebuilds source/layer/paint/filter/data
    map.setStyle(satellite ? STYLE_SATELLITE : STYLE_STREETS);
  });

  loadingEl.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('[data-init-retry]');
    if (target) void loadInitial();
  });

  // ---- initial load ----

  async function loadInitial(): Promise<void> {
    loadingEl.classList.remove('sub-hidden');
    loadingEl.textContent = 'Loading substrate…';
    try {
      allRows = await fetchAllSubstrate();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      loadingEl.innerHTML = `
        <div>Couldn't load substrate.</div>
        <div class="sub-pop-meta">${esc(message)}</div>
        <button data-init-retry="1" type="button">Retry</button>
      `;
      return;
    }
    cityIndex = buildCityIndex(allRows);
    view = { mode: 'overview' };
    refreshAll();
    fitToScope();
    loadingEl.classList.add('sub-hidden');
  }

  await loadInitial();
}
